import Link from "next/link";
import React, { ReactComponentElement, useMemo } from "react";
import { FiPackage } from "react-icons/fi";
import { IoMdTrash } from "react-icons/io";
import { MdAddCircleOutline } from "react-icons/md";
import InfiniteScroll from "react-infinite-scroll-component";

import { UIPack } from "../../pages/packs";
import { useScreenSize } from "../../styles/media-queries";
import { mutateWhere } from "../../utils/arrays";
import Loader from "../misc/Loader";
import { Subtitle } from "../misc/Typography";
import { PackRow } from "./PackRow";
import classNames from "classnames";
import { EmptyIllustration } from "../misc/EmptyIllustration";

export function GlobalSelectionActions({
  someSelected,
  allSelected,
  toggleAll,
  legend = false,
  onDelete,
  editable = false,
  compact = false,
}) {
  const show = compact || someSelected;
  const { phone, smallPhone } = useScreenSize();
  const showDate = compact && !phone;

  const showOverlayView = phone && !editable;

  const showLegend = legend && !someSelected;
  return (
    <div className={` mb-2 mt-4 ${!compact ? "w-full" : "w-full"} relative ${show ? "opacity-100" : "opacity-0"}`}>
      <div
        className={`${
          compact ? (phone ? "pl-10" : "pl-7") : "absolute right-full top-0"
        } z-20 lowercase text-sm h-full flex text-light-gray items-center`}
      >
        <div className={`flex items-center`}>
          {
            <input
              type="checkbox"
              onChange={toggleAll}
              checked={allSelected || someSelected || false}
              className={`group-hover:opacity-100 shrink-0 ${"w-5 h-5"} transition-opacity z-10 mr-4 ${
                !allSelected && someSelected && " opacity-50"
              } ${show ? "opacity-100" : someSelected ? "opacity-50" : "opacity-0"}`}
            />
          }
        </div>
        <div className="flex relative items-center grow">
          <div
            className={` flex items-center absolute left-0 w-full top-0 -translate-y-1/2 transition-opacity ${
              showLegend ? "opacity-100" : "opacity-0 pointer-events-none"
            } `}
          >
            {<div className="w-60 md:w-80 xl:w-96">Title</div>}
            <div className="shrink w-10 false md:block"></div>
            {showDate && <div className="w-32">upload date</div>}
            {/* {<div className='w-52'>Audio</div>} */}
          </div>

          {/* <IoMdDownload className=' opacity-50 hover:opacity-100 cursor-pointer w-5 h-5'/> */}
          {onDelete && (
            <IoMdTrash
              onClick={onDelete}
              className={`${
                showLegend ? "opacity-0 pointer-events-none" : "opacity-50 hover:opacity-100"
              } absolute left-0 top-0 -translate-y-1/2  cursor-pointer w-5 h-5 mr-2`}
            />
          )}
        </div>
      </div>
      {legend && (
        <div
          style={{ width: `calc(100% - 6.25rem)` }}
          className={`${phone ? "ml-16" : "ml-14"} relative left-1 h-px mt-2 bg-white opacity-20`}
        ></div>
      )}
    </div>
  );
}

export interface IPackListProps {
  packs: UIPack[];
  setPacks: (packs: UIPack[]) => void;
  selectable?: boolean;
  loadMore: () => void;
  hideCreator?: boolean;
  hasMore?: boolean;
  onDeletePack: (pack: UIPack) => void;
  prepend?: any;
  showPacksTitle?: boolean;
  canCreate?: boolean;
  onCreatePack?: () => void;

  // whether to add min-h-screen to pack list
  // set to false if you intend to render something under the packlist
  fullScreen?: boolean;

  emptyMessage: any;
}
export function PackList({
  packs,
  setPacks,
  selectable,
  loadMore,
  hideCreator,
  hasMore,
  onDeletePack,
  prepend,
  showPacksTitle,
  canCreate,
  onCreatePack,
  fullScreen,
  emptyMessage,
}: IPackListProps) {
  const mutatePack = (uiPack: UIPack, mutate: (t: UIPack) => UIPack) =>
    setPacks(mutateWhere(packs, mutate, (p) => p === uiPack || p.id === uiPack.id));
  const selectPack = (uiPack: UIPack) => mutatePack(uiPack, (t) => ({ ...t, selected: true }));
  const deselectPack = (uiPack: UIPack) => mutatePack(uiPack, (t) => ({ ...t, selected: false }));
  const allSelected = useMemo(() => packs.every((p) => p.selected), [packs]);
  const someSelected = useMemo(() => packs.some((p) => p.selected), [packs]);
  // @ts-ignore
  const toggleAll = () =>
    setPacks(
      mutateWhere(
        packs,
        (t) => ({ ...t, selected: !allSelected }),
        (t) => true
      )
    );

  // todo: batch delete
  const deleteSelected = async () => {
    for (const pack of packs) {
      if (pack.selected) {
        console.log(pack);
        await onDeletePack(pack);
      }
    }
  };

  return (
    <div
      className={
        "flex pb-16 items-center max-w-2xl m-auto w-2/3 flex-col  pt-12 " + classNames({ "min-h-screen": !!fullScreen })
      }
    >
      {prepend}
      <GlobalSelectionActions
        onDelete={onDeletePack && deleteSelected}
        someSelected={someSelected}
        allSelected={allSelected}
        toggleAll={toggleAll}
      />
      {showPacksTitle && (
        <div className="self-start justify-self-start w-full mt-12 flex items-center">
          <Subtitle className="max-w-2xl mx-auto text-left  flex items-center w-full">
            <FiPackage className="mr-2" />
            <div>Packs</div>
          </Subtitle>
          {canCreate && (
            <div
              onClick={onCreatePack}
              className="flex group items-center ml- opacity-50 hover:opacity-100 transition-opacity cursor-pointer select-none"
            >
              <div className="opacity-0 group-hover:opacity-100 transition whitespace-nowrap mr-2">Create new pack</div>
              <MdAddCircleOutline className="w-5 h-5 " />
            </div>
          )}
        </div>
      )}
      {packs?.length === 0 && emptyMessage}

      <InfiniteScroll
        scrollableTarget={"main"}
        dataLength={packs.length} //This is important field to render the next data
        next={loadMore}
        hasMore={hasMore}
        className="w-full overflow-visible"
        loader={<Loader />}
      >
        {/* existing */}
        {packs.map((uiPack, idx) => (
          <PackRow
            onDelete={() => onDeletePack(uiPack)}
            hideCreator={hideCreator}
            ownerNickname={uiPack?.creator?.nickname}
            key={`pack-${uiPack.id}#${idx}`}
            selectable={selectable}
            onSelect={() => selectPack(uiPack)}
            onDeselect={() => deselectPack(uiPack)}
            selected={uiPack.selected}
            {
              ...{} /* @ts-ignore */
            }
            pack={uiPack}
            onDelete={() => onDeletePack(uiPack)}
          />
        ))}
      </InfiniteScroll>
    </div>
  );
}
